import { attach, createEffect, createEvent, createStore, sample } from 'effector';
import { generateUuid } from '@lib/uuid';
import { GetMenuTreeProps, getWebnotesMenuTree, postCopyMenuItems } from '@nx-frontend/shared/api';
import { calculateMenuItemPositionType } from '@nx-frontend/shared/dtos';
import { generalEnv } from '@nx-frontend/shared/env';
import { $path } from '@nx-frontend/shared/store/portal-app';
import { PortalServiceMenuItemDto } from '@nx-frontend/shared/types';
import {
  MenuItem,
  MenuItemAddPayload,
  MenuItemChangeTypePayload,
  MenuItemChangeVisibilityPayload,
  MenuItemDeletePayload,
  MenuItemMovePayload,
  MenuItemType,
  MenuItemUpdatePayload,
  MenusState,
  MenuState,
  MenuType,
} from '@nx-frontend/shared/types';
import {
  addIdToOrder,
  getActiveIds,
  getItemChildrenIds,
  getItemParentIds,
  getMenuSlugs,
  isFolderMenuItem,
  prepareMenuItemsData,
  updateItemSlug,
} from './helpers';

export const localStorageExpandedItemsKey = 'menu-expanded-item-ids-ls';

export const initialMenuState: MenuState = {
  ids: [],
  entities: {},
  activeItemIds: [],
  rootOrder: [],
};

interface MenuConfigState {
  sessionId: string;
  workspaceId: string;
}
export const $menuConfig = createStore<MenuConfigState>({
  sessionId: '',
  workspaceId: '',
});
export const setMenuConfigStateEvent = createEvent<MenuConfigState>();

$menuConfig.on(setMenuConfigStateEvent, (_, menuConfig) => menuConfig);

export const fetchMenuTreeFx = createEffect(async (params: GetMenuTreeProps) => {
  if (params.menuType === MenuType.WEBNOTES_MENU) {
    const items = await getWebnotesMenuTree({ ...params, type: 'note', orderBy: 'title', orderDirection: 'ASC' });
    return {
      items,
      menuType: params.menuType,
      parentId: params.parentKey,
      forceReload: params.forceReload,
    };
  } else if (params.menuType === MenuType.NIMBUS_CONTENT) {
    return {
      items: await getWebnotesMenuTree({ ...params }),
      menuType: params.menuType,
      parentId: params.parentKey,
      forceReload: params.forceReload,
    };
  } else if (
    params.menuType === MenuType.PORTAL_SIDEBAR ||
    params.menuType === MenuType.PORTAL_TOPBAR ||
    params.menuType === MenuType.PORTAL_FOOTER
  ) {
    return {
      items: [] as PortalServiceMenuItemDto[],
      menuType: params.menuType,
      parentId: params.parentKey,
    };
  } else {
    throw Error('[fetchMenuTreeFx]: not supported MenuType');
  }
});

export const setExpandedMenuItemsFx = createEffect((itemIds: string[]) => {
  const expandedItemIds: string[] = JSON.parse(localStorage.getItem(localStorageExpandedItemsKey) ?? '[]');
  itemIds.forEach((itemId) => {
    if (!expandedItemIds.includes(itemId)) {
      expandedItemIds.push(itemId);
    }
  });
  localStorage.setItem(localStorageExpandedItemsKey, JSON.stringify(expandedItemIds));
  return expandedItemIds;
});

export const toggleExpandItemFx = attach({
  source: [$menuConfig],
  effect: (
    [menuConfig],
    {
      item,
      menuType,
      workspaceId,
    }: {
      item: MenuItem;
      menuType: MenuType;
      workspaceId?: string;
    }
  ) => {
    toggleExpandedMenuItemId(item.id);

    let expandedItemIds: string[] = JSON.parse(localStorage.getItem(localStorageExpandedItemsKey) ?? '[]');
    const isExpanded = expandedItemIds.includes(item.id);
    if (isExpanded) {
      expandedItemIds = expandedItemIds.filter((id) => id !== item.id);
    } else {
      expandedItemIds = [...expandedItemIds, item.id];
    }
    localStorage.setItem(localStorageExpandedItemsKey, JSON.stringify(expandedItemIds));

    const gatewayUrl = generalEnv.gatewayUrl;
    if (item.hasChildren && !isExpanded) {
      return fetchMenuTreeFx({
        gatewayUrl,
        workspaceId: workspaceId || menuConfig.workspaceId,
        menuType: menuType,
        depth: 1,
        parentKey: item.id,
      });
    }
    return {
      items: [],
      menuType,
    };
  },
});

export const initExpandedMenuItemsFx = createEffect(() => {
  return JSON.parse(localStorage.getItem(localStorageExpandedItemsKey) ?? '[]');
});
export const initCustomizerMenuEvent = createEvent();
export const setWebnotesMenuEvent = createEvent<MenuState | null>();
export const setActiveItemIdEvent = createEvent<{ id: string | null; menuType: MenuType }>();
export const toggleActiveItemIdEvent = createEvent<{ id: string; menuType: MenuType }>();
export const addMenuItemEvent = createEvent<MenuItemAddPayload>();
export const deleteMenuItemEvent = createEvent<MenuItemDeletePayload>();
export const updateMenuItemEvent = createEvent<MenuItemUpdatePayload>();
export const updateMenuItemVisibilityEvent = createEvent<MenuItemChangeVisibilityPayload>();
export const updateMenuItemTypeEvent = createEvent<MenuItemChangeTypePayload>();
export const resetMenuActiveItemIdsEvent = createEvent<MenuType>();
export const moveMenuItemEvent = createEvent<MenuItemMovePayload>();
export const resetMenuItemsEvent = createEvent<{ menuType: MenuType; menuState?: MenuState }>();
export const updateMenuItemSlugEvent = createEvent<{ menuType: MenuType; item: Pick<MenuItem, 'id' | 'slug'> }>();
export const resetTempMenuItemsDataEvent = createEvent();
export const webnotesAddMenuItemEvent = createEvent<MenuItemAddPayload>();
export const removeMenuItemsDraftMarkEvent = createEvent();
export const addUserIdsToAllAllowedAllMenuItemsEvent = createEvent<number[]>();

export const duplicateMenuItemFx = attach({
  source: [$path],
  effect: async (
    [path],
    {
      srcId,
      newName,
      portalId,
      parentMenuItemId,
      menuType = MenuType.PORTAL_SIDEBAR,
      isSyncedCopy,
    }: {
      srcId: string;
      newName: string;
      portalId: string;
      parentMenuItemId?: string;
      menuType?: MenuType;
      isSyncedCopy?: boolean;
    }
  ) => {
    const newId = generateUuid();
    await postCopyMenuItems({
      srcMenuItemId: srcId,
      destinationPortalId: portalId,
      destinationMenuItemId: newId,
      destinationMenuItemName: newName,
      destinationParentMenuItemId: parentMenuItemId,
      destinationPositionType: calculateMenuItemPositionType(menuType),
      permissions: 'allowAll',
      isSyncedCopy,
    });

    return { path, menuType, newId };
  },
});

export const initialMenusState: MenusState = {
  [MenuType.WEBNOTES_MENU]: initialMenuState,
  [MenuType.PORTAL_SIDEBAR]: initialMenuState,
  [MenuType.PORTAL_TOPBAR]: initialMenuState,
  [MenuType.PORTAL_FOOTER]: initialMenuState,
  [MenuType.NIMBUS_CONTENT]: initialMenuState,
  [MenuType.NODE_PICKER_CONTENT]: initialMenuState,
};

export const $webnotesDumpMenu = createStore<MenuState | null>(null);

export const setMenuTreeEvent = createEvent<{
  items: PortalServiceMenuItemDto[];
  menuType: MenuType;
  parentId?: string | string[];
  forceReload?: boolean;
}>();

sample({
  source: fetchMenuTreeFx.doneData,
  target: setMenuTreeEvent,
});

export const $menuState = createStore(initialMenusState)
  .on(setMenuTreeEvent, (state, { items, menuType, parentId, forceReload }) => {
    const { items: itemsMap } = prepareMenuItemsData(items);
    const menu = state[menuType] ?? initialMenuState;
    const isParentIdArray = Array.isArray(parentId);

    if (menuType === MenuType.WEBNOTES_MENU) {
      if (parentId && !isParentIdArray) {
        const parent = state[menuType].entities[parentId];
        const entities = { ...menu.entities, ...itemsMap };
        entities[parentId].order = [
          ...(parent.order ?? []),
          ...Object.values(itemsMap)
            .filter((item) => !parent.order?.includes(item.id))
            .map((item) => item.id),
        ];
        entities[parentId].order?.forEach((id, index) => {
          if (entities[id]) {
            entities[id].index = index;
          }
        });
        return { ...state, [menuType]: { ...menu, entities, ids: Object.keys(entities) } };
      }
    } else {
      const entities = forceReload ? { ...itemsMap } : { ...menu.entities, ...itemsMap };
      const rootOrder = Object.values(entities)
        .filter((item) => item.parentId === '')
        .sort((a, b) => a.index - b.index)
        .map((item) => item.id);
      if (parentId && !isParentIdArray && items.length) {
        entities[parentId].order = Object.values(itemsMap)
          .filter((item) => item.parentId === parentId)
          .sort((a, b) => a.index - b.index)
          .map((item) => item.id);
      }

      return { ...state, [menuType]: { ...menu, entities, ids: Object.keys(entities), rootOrder } };
    }

    return state;
  })
  .on(setActiveItemIdEvent, (state, { id, menuType }) => {
    const menu = state[menuType];
    const updatedState = {
      ...state,
      [MenuType.PORTAL_TOPBAR]: { ...state[MenuType.PORTAL_TOPBAR], activeItemIds: [] },
      [MenuType.PORTAL_SIDEBAR]: { ...state[MenuType.PORTAL_SIDEBAR], activeItemIds: [] },
      [MenuType.PORTAL_FOOTER]: { ...state[MenuType.PORTAL_FOOTER], activeItemIds: [] },
      [MenuType.WEBNOTES_MENU]: { ...state[MenuType.WEBNOTES_MENU], activeItemIds: [] },
      [menuType]: { ...menu, activeItemIds: [id] },
    };
    return { ...updatedState };
  })
  .on(toggleActiveItemIdEvent, (state, { id, menuType }) => {
    const menu = state[menuType];
    return { ...state, [menuType]: { ...menu, activeItemIds: getActiveIds(menu.entities, menu.activeItemIds, id) } };
  })
  .on(addMenuItemEvent, (state, { menuType, item }) => {
    const menu = state[menuType];
    const entities = { ...menu.entities };

    //Add posistion for duplicate menu item
    const newPositionType = calculateMenuItemPositionType(menuType);
    item.positionType = newPositionType;

    entities[item.id] = item;
    const rootOrder = menu.rootOrder;
    if (!item.parentId && !rootOrder.includes(item.id)) {
      rootOrder.splice(item.index, 0, item.id);
    } else {
      const parentItem = entities[item.parentId];
      if (parentItem) {
        const order = parentItem.order ?? [];
        if (!order.includes(item.id)) {
          order.splice(item.index, 0, item.id);
          parentItem.order = order;
          parentItem.hasChildren = true;
          entities[parentItem.id] = parentItem;
        }
      }
    }
    return { ...state, [menuType]: { ...menu, entities, ids: Object.keys(entities), rootOrder } };
  })
  .on(webnotesAddMenuItemEvent, (state, { menuType, item }) => {
    const menu = state[menuType];
    const entities = { ...menu.entities };
    entities[item.id] = item;
    const rootOrder = menu.rootOrder;
    if (!item.parentId && !rootOrder.includes(item.id)) {
      const toUpdateIndexIds = rootOrder.slice(item.index);
      toUpdateIndexIds.forEach((itemId) => {
        entities[itemId] = { ...entities[itemId], ...{ index: entities[itemId].index + 1 } };
      });
      rootOrder.splice(item.index, 0, item.id);
    } else {
      const parentItem = entities[item.parentId];
      if (parentItem) {
        const order = parentItem.order ?? [];
        if (!order.includes(item.id)) {
          const toUpdateIndexIds = order.slice(item.index);
          toUpdateIndexIds.forEach((itemId) => {
            if (entities[itemId]) {
              entities[itemId] = { ...entities[itemId], ...{ index: entities[itemId].index + 1 } };
            }
          });
          order.splice(item.index, 0, item.id);
          parentItem.order = order;
          parentItem.hasChildren = true;
          entities[parentItem.id] = parentItem;
        }
      }
    }
    return { ...state, [menuType]: { ...menu, entities, ids: Object.keys(entities), rootOrder } };
  })
  .on(deleteMenuItemEvent, (state, { menuType, item, soft }) => {
    const menu = state[menuType];
    const entities = { ...menu.entities };
    if (soft) {
      entities[item.id] = { ...entities[item.id], deleted: true };
    } else {
      delete entities[item.id];
    }
    let rootOrder = menu.rootOrder;
    if (!item.parentId.length) {
      rootOrder = rootOrder.filter((orderId) => orderId !== item.id);
      rootOrder.forEach((id, index) => {
        const child = entities[id];
        if (child) {
          entities[id] = { ...child, index };
        }
      });
    } else {
      const parentItem = { ...entities[item.parentId] };
      if (parentItem.order) {
        parentItem.order = parentItem.order.filter((orderId) => orderId !== item.id);
        if (!parentItem.order.length) {
          parentItem.hasChildren = false;
          // parentItem.expanded = false; TODO: remove
        } else {
          parentItem.order.forEach((id, index) => {
            const child = entities[id];
            if (child) {
              entities[id] = { ...child, index };
            }
          });
        }
        entities[item.parentId] = parentItem;
      }
    }
    return {
      ...state,
      [menuType]: {
        ...menu,
        entities,
        ids: Object.keys(entities),
        rootOrder,
        activeItemIds: menu.activeItemIds,
      },
    };
  })
  .on(updateMenuItemEvent, (state, { menuType, item }) => {
    const menu = state[menuType];
    const entities = { ...menu.entities };
    const oldItem: MenuItem = entities[item.id];
    if (oldItem) {
      entities[item.id] = { ...oldItem, ...item } as MenuItem;
    }
    return {
      ...state,
      [menuType]: {
        ...menu,
        entities: { ...entities },
      },
    };
  })
  .on(updateMenuItemVisibilityEvent, (state, { menuType, item }) => {
    const menu = state[menuType];
    const entities = { ...menu.entities };
    const entity = entities[item.id];
    if (entity) {
      entities[item.id] = { ...entity, visible: item.visible };
    }
    return {
      ...state,
      [menuType]: {
        ...menu,
        entities: { ...entities },
      },
    };
  })
  .on(updateMenuItemTypeEvent, (state, { menuType, item: { id, ...updProps } }) => {
    const menu = state[menuType];
    const entities = { ...menu.entities };
    const entity = entities[id];
    if (entity) {
      entities[id] = { ...entity, ...updProps };
    }
    return {
      ...state,
      [menuType]: {
        ...menu,
        entities: { ...entities },
      },
    };
  })
  .on(resetMenuActiveItemIdsEvent, (state, menuType) => {
    const menu = state[menuType];
    return { ...state, [menuType]: { ...menu, activeItemIds: [] } };
  })
  .on(moveMenuItemEvent, (state, { id, index, parentId, menuType, oldMenuType }) => {
    const menu = state[menuType];
    const menuEntities = { ...menu.entities };
    let rootOrder = [...menu.rootOrder];
    if (menuType === oldMenuType) {
      const item = { ...menuEntities[id] };
      const itemParentId = item.parentId;

      // Add item id to new parent order
      if (parentId.length) {
        const newParent = menuEntities[parentId];
        const newOrder = addIdToOrder(newParent.order ?? [], id, index);
        menuEntities[parentId] = { ...newParent, order: newOrder };
      } else {
        rootOrder = addIdToOrder(rootOrder, id, index);
      }

      // Delete item id from old parent order
      if (parentId !== itemParentId) {
        if (itemParentId?.length) {
          const oldParent = menuEntities[itemParentId];
          const oldParentOrder = oldParent.order?.filter((childId) => childId !== id);
          if (oldParentOrder) {
            menuEntities[itemParentId] = { ...oldParent, order: oldParentOrder };
          }
        } else {
          rootOrder = rootOrder.filter((childId) => childId !== id);
        }
      }

      // Update indexes of all affected items
      rootOrder.forEach((childId, childIndex) => {
        const child = menuEntities[childId];
        menuEntities[childId] = { ...child, index: childIndex };
      });
      if (parentId.length) {
        const parentItem = menuEntities[parentId];
        parentItem.order?.forEach((childId, childIndex) => {
          const child = menuEntities[childId];
          menuEntities[childId] = { ...child, index: childIndex };
        });
      }
      if (itemParentId?.length && item.parentId !== parentId) {
        const parentItem = menuEntities[item.parentId];
        parentItem.order?.forEach((childId, childIndex) => {
          const child = menuEntities[childId];
          menuEntities[childId] = { ...child, index: childIndex };
        });
      }

      if (parentId !== itemParentId) {
        const item = menuEntities[id];
        const newParentItem = menuEntities[parentId];
        const newUrl = (newParentItem?.url ?? '') + '/' + item.slug;
        menuEntities[id] = { ...item, parentId, url: newUrl, originalUrl: item.originalUrl ?? item.url };
        const childrenIds = getItemChildrenIds(menuEntities, id);
        childrenIds.forEach((childId) => {
          const childItem = menuEntities[childId];
          if (childItem) {
            const childParentItem = menuEntities[childItem.parentId];
            const childNewUrl = childParentItem.url + '/' + childItem.slug;
            menuEntities[childItem.id] = {
              ...childItem,
              url: childNewUrl,
              originalUrl: childItem.originalUrl ?? childItem.url,
            };
          }
        });
      }

      return {
        ...state,
        [menuType]: {
          ...menu,
          rootOrder: [...rootOrder],
          entities: { ...menuEntities },
          ids: Object.keys(menuEntities),
        },
      };
    } else {
      const oldMenu = state[oldMenuType];
      const oldMenuEntities = { ...oldMenu.entities };
      const item = oldMenuEntities[id];
      let oldMenuRootOrder = [...oldMenu.rootOrder];
      const newPositionType = calculateMenuItemPositionType(menuType);

      // Add item id to new parent order
      if (parentId.length) {
        const newParent = menuEntities[parentId];
        const newOrder = addIdToOrder(newParent.order ?? [], id, index);
        menuEntities[parentId] = { ...newParent, order: newOrder, positionType: newPositionType };
      } else {
        rootOrder = addIdToOrder(rootOrder, id, index);
      }

      // Delete item id from old parent order
      if (item.parentId.length) {
        const oldParent = oldMenuEntities[item.parentId];
        const oldParentOrder = oldParent.order?.filter((childId) => childId !== id);
        if (oldParentOrder) {
          oldMenuEntities[item.parentId] = { ...oldParent, order: oldParentOrder };
        }
      } else {
        oldMenuRootOrder = oldMenuRootOrder.filter((childId) => childId !== id);
      }

      [...getItemChildrenIds(oldMenuEntities, id), id].forEach((moveId) => {
        if (id === moveId) {
          menuEntities[moveId] = { ...oldMenuEntities[moveId], parentId, positionType: newPositionType };
        } else {
          menuEntities[moveId] = { ...oldMenuEntities[moveId], positionType: newPositionType };
        }
        delete oldMenuEntities[moveId];
      });

      // Update indexes of all affected items
      rootOrder.forEach((childId, childIndex) => {
        const child = menuEntities[childId];
        menuEntities[childId] = { ...child, index: childIndex, positionType: newPositionType };
      });
      oldMenuRootOrder.forEach((childId, childIndex) => {
        const child = oldMenuEntities[childId];
        oldMenuEntities[childId] = { ...child, index: childIndex };
      });
      if (parentId.length) {
        const parentItem = menuEntities[parentId];
        parentItem.order?.forEach((childId, childIndex) => {
          const child = menuEntities[childId];
          menuEntities[childId] = { ...child, index: childIndex, positionType: newPositionType };
        });
      }
      if (item.parentId.length) {
        const parentItem = oldMenuEntities[item.parentId];
        parentItem.order?.forEach((childId, childIndex) => {
          const child = oldMenuEntities[childId];
          oldMenuEntities[childId] = { ...child, index: childIndex };
        });
      }

      if (parentId !== item.parentId) {
        const newParentItem = menuEntities[parentId];
        const newUrl = (newParentItem?.url ?? '') + '/' + item.slug;
        menuEntities[id] = { ...item, parentId, url: newUrl, originalUrl: item.originalUrl ?? item.url };
        const childrenIds = getItemChildrenIds(menuEntities, id);
        childrenIds.forEach((childId) => {
          const childItem = menuEntities[childId];
          if (childItem) {
            const childParentItem = menuEntities[childItem.parentId];
            const childNewUrl = childParentItem.url + '/' + childItem.slug;
            menuEntities[childItem.id] = {
              ...childItem,
              url: childNewUrl,
              originalUrl: childItem.originalUrl ?? childItem.url,
            };
          }
        });
      }

      return {
        ...state,
        [menuType]: {
          ...menu,
          rootOrder: [...rootOrder],
          entities: { ...menuEntities },
          ids: Object.keys(menuEntities),
        },
        [oldMenuType]: {
          ...oldMenu,
          rootOrder: [...oldMenuRootOrder],
          entities: { ...oldMenuEntities },
          ids: Object.keys(oldMenuEntities),
        },
      };
    }
  })
  .on(resetMenuItemsEvent, (state, { menuType, menuState }) => {
    return { ...state, [menuType]: menuState ? menuState : initialMenuState };
  })
  .on(updateMenuItemSlugEvent, (state, { menuType, item }) => {
    const menu = state[menuType];
    const entities = { ...menu.entities };
    const updatedItems = updateItemSlug(state, menuType, item);
    updatedItems.forEach((item) => {
      entities[item.id] = item;
    });
    return {
      ...state,
      [menuType]: {
        ...state[menuType],
        entities: { ...entities },
        ids: Object.keys(entities),
      },
    };
  })
  .on(resetTempMenuItemsDataEvent, (state) => {
    const newState = { ...state };
    Object.keys(state).forEach((menuTypeItem) => {
      const menuType = menuTypeItem as MenuType;
      const items = Object.values(state[menuType].entities);
      items.forEach((item) => {
        delete newState[menuType].entities[item.id]['originalUrl'];
      });
    });
    return { ...newState };
  })
  .on(removeMenuItemsDraftMarkEvent, (state) => {
    const newState = { ...state };
    Object.keys(state).forEach((menuTypeItem) => {
      const menuType = menuTypeItem as MenuType;
      const items = Object.values(state[menuType].entities);
      items.forEach((item) => {
        if (item.isDraft) {
          newState[menuType].entities[item.id].isDraft = false;
        }
      });
    });
    return { ...newState };
  })
  .on(addUserIdsToAllAllowedAllMenuItemsEvent, (state, userIds) => {
    const newState = { ...state };
    Object.keys(state).forEach((menuTypeItem) => {
      const menuType = menuTypeItem as MenuType;
      const items = Object.values(state[menuType].entities);
      items.forEach((item) => {
        const permissions = item.permissions;
        if (permissions?.allowUserIds?.length) {
          newState[menuType].entities[item.id] = {
            ...item,
            permissions: {
              ...permissions,
              allowUserIds: [...permissions.allowUserIds, ...userIds],
            },
          };
        }
      });
    });
    return { ...newState };
  })
  .on(setWebnotesMenuEvent, (state, menu) => {
    const newState = { ...state };
    if (!state[MenuType.WEBNOTES_MENU].ids.length && menu) {
      newState[MenuType.WEBNOTES_MENU] = menu;
    }
    return newState;
  });

sample({
  clock: initCustomizerMenuEvent,
  source: $webnotesDumpMenu,
  target: setWebnotesMenuEvent,
});

sample({
  source: $menuState,
  fn: (menuState) => {
    const webnotesMenu = menuState[MenuType.WEBNOTES_MENU];
    return webnotesMenu.ids.length === 0 ? $webnotesDumpMenu.getState() : webnotesMenu;
  },
  target: $webnotesDumpMenu,
});

export const setActiveItemIdBasedOnPathFx = attach({
  source: [$menuState],
  effect: ([menusState], path) => {
    Object.keys(menusState).forEach((type) => {
      const menuType = type as MenuType;
      const entities = menusState[menuType].entities;
      Object.values(entities).forEach((item) => {
        if (item.url === path.toLowerCase()) {
          const parentIds = getItemParentIds(entities, item.id);
          addExpandedMenuItemIds(parentIds);
          setActiveItemIdEvent({ id: item.id, menuType: menuType });
        }
      });
    });
  },
});
export const getMenuItemsByParentIdFx = attach({
  source: [$menuState],
  effect: (
    [menuState]: [MenusState],
    { menuType, parentId, includeParent }: { menuType: MenuType; parentId: string; includeParent?: boolean }
  ) => {
    const menu = menuState[menuType];
    const entities = menu.entities;
    const parent = entities[parentId];
    const items: MenuItem[] = [];
    if (includeParent) {
      items.push(parent);
    }
    if (parent && parent.order && parent.order.length) {
      const childIds = getItemChildrenIds(entities, parentId);
      const childItems = childIds.reduce((acc, id) => {
        const item = entities[id];
        if (item) {
          acc.push(item);
        }
        return acc;
      }, [] as MenuItem[]);
      items.push(...childItems);
    }
    return items;
  },
});

export const getMenusExistedSlugsFx = attach({
  source: [$menuState],
  effect: ([menuState]: [MenusState], menuTypes: MenuType[]) => {
    return getMenuSlugs(menuState, menuTypes);
  },
});

export const getIsActiveMenuItemIdFx = createEffect(({ itemId }: { itemId: string }) => {
  const expandedItemIdsJson = localStorage.getItem(localStorageExpandedItemsKey);
  const expandedMenuItems: string[] | null = expandedItemIdsJson ? JSON.parse(expandedItemIdsJson) : null;
  return expandedMenuItems?.includes(itemId) ?? false;
});

export const getMenuItemsByParentDeepIdFx = createEffect(
  async ({ menuType, parentId, includeParent }: { menuType: MenuType; parentId: string; includeParent: boolean }) => {
    const collectAllItems = async (
      menuType: MenuType,
      parentId: string,
      includeParent: boolean,
      processedIds: Set<string>
    ): Promise<MenuItem[]> => {
      const items: MenuItem[] = await getMenuItemsByParentIdFx({ menuType, parentId, includeParent });
      let allItems: MenuItem[] = [...items];

      for (const item of items) {
        if (item.hasChildren && !processedIds.has(item.id)) {
          processedIds.add(item.id);
          const childItems = await collectAllItems(menuType, item.id, includeParent, processedIds);
          allItems = [...allItems, ...childItems];
        }
      }

      return allItems;
    };

    const allItems = await collectAllItems(menuType, parentId, includeParent, new Set<string>());
    const uniqueItems = Array.from(new Map(allItems.map((item) => [item.id, item])).values());

    return uniqueItems;
  }
);

export const getHasDraftDeepFx = createEffect(
  async ({ menuType, parentId, includeParent }: { menuType: MenuType; parentId: string; includeParent: boolean }) => {
    const items = await getMenuItemsByParentDeepIdFx({ menuType, parentId, includeParent });
    const hasDraft = items.some((item) => item.isDraft);
    return hasDraft;
  }
);

export const getMenuItemsByUrlFx = attach({
  source: [$menuState],
  effect: ([menuState]: [MenusState], { menuTypes, url }: { menuTypes: MenuType[]; url: string }) => {
    const itemsWithMenuType = menuTypes.flatMap((menuType) =>
      Object.values(menuState[menuType].entities).map((item) => ({
        item: { ...item },
        menuType: menuType,
      }))
    );

    return itemsWithMenuType.find((obj) => {
      return obj.item.url === url;
    });
  },
});

export const getMenuItemsByMenuTypesFx = attach({
  source: [$menuState],
  effect: ([menuState]: [MenusState], { menuTypes }: { menuTypes: MenuType[] }) => {
    return menuTypes.flatMap((menuType) => Object.values(menuState[menuType].entities));
  },
});

export const getMenuItemsByIdFx = attach({
  source: [$menuState],
  effect: ([menuState]: [MenusState], { menuTypes, id }: { menuTypes: MenuType[]; id: string }) => {
    const itemsWithMenuType = menuTypes.flatMap((menuType) =>
      Object.values(menuState[menuType].entities).map((item) => ({
        item: { ...item },
        menuType: menuType,
      }))
    );

    return itemsWithMenuType.find((obj) => {
      return obj.item.id === id;
    });
  },
});

export const getMenuItemsByPageIdFx = attach({
  source: [$menuState],
  effect: ([menuState]: [MenusState], { menuTypes, pageId }: { menuTypes: MenuType[]; pageId: string }) => {
    const itemsWithMenuType = menuTypes.flatMap((menuType) =>
      Object.values(menuState[menuType].entities).map((item) => ({
        item: { ...item },
        menuType: menuType,
      }))
    );

    return itemsWithMenuType.find((obj) => {
      return obj.item.pageId === pageId;
    });
  },
});

export const searchMenuItemByIdFx = attach({
  source: [$menuState],
  effect: ([menusState]: [MenusState], { menuTypes, id }: { menuTypes: MenuType[]; id: string }) => {
    for (const menuType of menuTypes) {
      const menu = menusState[menuType];
      const entities = menu.entities;
      if (entities[id] != null) {
        return { item: entities[id], menuType };
      }
    }
    return;
  },
});

export const getMenuItemByIdFx = attach({
  source: [$menuState],
  effect: ([menusState]: [MenusState], { menuType, id }: { menuType: MenuType; id: string }) => {
    const menu = menusState[menuType];
    const entities = menu.entities;
    return entities[id] || undefined;
  },
});

export const getMenuItemIncludesFoldersFx = attach({
  source: [$menuState],
  effect: ([menusState]: [MenusState], { menuType, id }: { menuType: MenuType; id: string }) => {
    const menu = menusState[menuType];
    const entities = menu.entities;
    const item = entities[id];
    if (item.order) {
      return item.order.some((childId) => {
        const child = entities[childId];
        return child && isFolderMenuItem(child.type);
      });
    }
    return false;
  },
});

export const getMenuItemByTargetIdFx = attach({
  source: [$menuState],
  effect: ([menusState]: [MenusState], { targetId }: { targetId: string }) => {
    for (const menuType of Object.values(MenuType)) {
      const menuState: MenuState = menusState[menuType];
      if (menuState) {
        if ('entities' in menuState) {
          for (const entityId in menuState.entities) {
            const menuItem: MenuItem = menuState.entities[entityId];
            if ('targetId' in menuItem && menuItem.targetId === targetId) {
              return {
                menuItem,
                menuType,
              };
            }
          }
        }
      }
    }
    return undefined;
  },
});

export const getMaxIndexFx = attach({
  source: [$menuState],
  effect: ([menusState]: [MenusState], { menuType }: { menuType: MenuType }) => {
    const menu: MenuState | undefined = menusState[menuType];
    if (!menu || !menu.entities) {
      return null;
    }
    return menu.rootOrder.length;
  },
});

export const getMenuRootOrderFx = attach({
  source: [$menuState],
  effect: ([menusState]: [MenusState], { menuType }: { menuType: MenuType }) => {
    const menu = menusState[menuType];
    return menu.rootOrder;
  },
});

export const getOrderByIdFx = attach({
  source: [$menuState],
  effect: ([menusState]: [MenusState], { menuType, id }: { menuType: MenuType; id: string }) => {
    const menu = menusState[menuType];
    if (id.length) {
      const entities = menu.entities;
      return entities[id].order;
    } else {
      return menu.rootOrder;
    }
  },
});

export const getPortalMenuItemWithPermissionsCountFx = attach({
  source: [$menuState],
  effect: ([menusState]: [MenusState]) => {
    const menuTypes = [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER];
    return menuTypes.reduce((acc, menuType) => {
      const menu = menusState[menuType];
      Object.values(menu.entities).forEach((menuItem) => {
        if (menuItem.permissions?.allowUserIds.length) {
          acc++;
        }
      });
      return acc;
    }, 0);
  },
});

export const $menuSessionId = createStore<string>('');

export const $menuWorkspaceId = createStore<string>('');

export const setMenuWorkspaceIdEvent = createEvent<string>();

$menuWorkspaceId.on(setMenuWorkspaceIdEvent, (_, workspaceId) => workspaceId);

export const toggleExpandedMenuItemId = createEvent<string>();
export const addExpandedMenuItemIds = createEvent<string[]>();
export const $menuExpandedItemIds = createStore<string[]>([])
  .on(toggleExpandedMenuItemId, (state, id) => {
    if (state.includes(id)) {
      return state.filter((stateId) => stateId !== id);
    } else {
      return [...state, id];
    }
  })
  .on(addExpandedMenuItemIds, (state, ids) => {
    const result = ids.reduce(
      (acc, id) => {
        if (!acc.includes(id)) {
          acc.push(id);
        }
        return acc;
      },
      [...state]
    );
    return [...result];
  })
  .on(initExpandedMenuItemsFx.doneData, (state, ids: string[]) => {
    return [...ids];
  })
  .on(setExpandedMenuItemsFx.doneData, (state, ids) => {
    return [...ids];
  });

export const getIsMenuItemExpandedFx = attach({
  source: [$menuExpandedItemIds],
  effect: ([ids]: [string[]], { id }: { id: string }) => {
    return ids.includes(id);
  },
});

export const setExpandedParentsByMenuItemIdFx = attach({
  source: [$menuState],
  effect: async ([menuState], { menuItemId, menuTypes }: { menuItemId: string; menuTypes: MenuType[] }) => {
    const result = await searchMenuItemByIdFx({ id: menuItemId, menuTypes });
    if (result) {
      const parentIds = getItemParentIds(menuState[result.menuType].entities, menuItemId);
      if (parentIds.length) {
        setExpandedMenuItemsFx(parentIds);
        return parentIds;
      }
    }
    return [] as string[];
  },
});

export const $existingSlugs = createStore<string[]>([]).on($menuState, (existingSlugs, menuState) => {
  const allSlugs = Object.values(menuState).flatMap((menu) => Object.values(menu.entities).map((item) => item.slug));
  return Array.from(new Set(allSlugs));
});

export const getMenuItemByFieldFx = attach({
  source: [$menuState],
  effect: (
    [menusState]: [MenusState],
    { fieldName, fieldValue }: { fieldName: keyof MenuItem; fieldValue: string }
  ) => {
    for (const menuType of Object.values(MenuType)) {
      const menuState: MenuState = menusState[menuType];
      if (menuState) {
        if ('entities' in menuState) {
          for (const entityId in menuState.entities) {
            const menuItem: MenuItem = menuState.entities[entityId];
            if (fieldName in menuItem && menuItem[fieldName] === fieldValue) {
              return {
                menuItem,
                menuType,
              };
            }
          }
        }
      }
    }
    return undefined;
  },
});

/**
 * Flag expandInProcess needs to cansele expandAllItemsFx effect
 */
let expandInProgress = false;

export const expandAllItemsFx = attach({
  source: [$menuConfig, $menuState],
  effect: async (
    [menuConfig, menuState],
    {
      menuType,
      workspaceId,
      itemTypes = [],
      collapseAllItems,
    }: {
      menuType: MenuType;
      workspaceId?: string;
      itemTypes?: MenuItemType[];
      collapseAllItems: (params: { menuType: MenuType }) => Promise<{ items: never[]; menuType: MenuType }>;
    }
  ) => {
    expandInProgress = true;
    const gatewayUrl = generalEnv.gatewayUrl;
    const toExpandIds = menuState[menuType].ids.filter(
      (id) =>
        (itemTypes.length && itemTypes.includes(menuState[menuType].entities[id].type)) ||
        menuState[menuType].entities[id].type === MenuItemType.NOTES_FOLDER
    );
    let expandedItemIds: string[] = JSON.parse(localStorage.getItem(localStorageExpandedItemsKey) ?? '[]');
    const items: never[] = [];

    for (const id of toExpandIds) {
      if (expandInProgress) {
        const isExpanded = expandedItemIds.includes(id);
        if (!isExpanded) {
          toggleExpandedMenuItemId(id);
          expandedItemIds = [...expandedItemIds, id];
          if (menuState[menuType].entities[id].hasChildren) {
            const { items: children } = await fetchMenuTreeFx({
              gatewayUrl,
              workspaceId: workspaceId || menuConfig.workspaceId,
              menuType: menuType,
              depth: 1,
              parentKey: id,
            });
            items.push(...(children as never[]));
          }
        }
      } else {
        break;
      }
    }

    localStorage.setItem(localStorageExpandedItemsKey, JSON.stringify(expandedItemIds));

    if (!expandInProgress) {
      collapseAllItems({ menuType });
      return {
        items: [],
        menuType,
      };
    }

    return {
      items,
      menuType,
    };
  },
});

export const collapseAllItemsFx = attach({
  source: [$menuState, $menuExpandedItemIds],
  effect: async (
    [menuState, menuExpandedItemIds],
    {
      menuType,
      itemTypes = [],
    }: {
      menuType: MenuType;
      itemTypes?: MenuItemType[];
    }
  ) => {
    expandInProgress = false; // cancel expandAllItemsFx effect

    const toCollapseIds = menuState[menuType].ids.filter(
      (id) =>
        (itemTypes.length && itemTypes.includes(menuState[menuType].entities[id].type)) ||
        menuState[menuType].entities[id].type === MenuItemType.NOTES_FOLDER
    );
    let expandedItemIds: string[] = menuExpandedItemIds;
    for (const id of toCollapseIds) {
      const isExpanded = expandedItemIds.includes(id);
      if (isExpanded) {
        toggleExpandedMenuItemId(id);
        expandedItemIds = expandedItemIds.filter((itemId) => itemId !== id);
      }
    }
    localStorage.setItem(localStorageExpandedItemsKey, JSON.stringify(expandedItemIds));

    return {
      items: [],
      menuType,
    };
  },
});
